/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationSubscriptionProps {
  id: string;
  name: string;
  unitId: string;
  notificationGroupId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationSubscriptionRecord extends Record<NotificationSubscriptionProps>({
  id: null as any,
  name: null as any,
  unitId: null as any,
  notificationGroupId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    unitId: string;
    notificationGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationSubscriptionRecord {
    if (json == null) {
      throw new Error('NotificationSubscriptionRecord.fromJS: parameter cannot be null');
    }
    return new NotificationSubscriptionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationSubscriptionJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NotificationSubscriptionJson.name'),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'NotificationSubscriptionJson.unitId',
      ),
      notificationGroupId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
        'NotificationSubscriptionJson.notificationGroupId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'NotificationSubscriptionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    unitId: string;
    notificationGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationSubscriptionRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationSubscriptionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationSubscriptionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationSubscriptionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NotificationSubscriptionJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'NotificationSubscriptionJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationGroupId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'notificationGroupId', '00000000-0000-0000-0000-000000000000'),
            'NotificationSubscriptionJson.notificationGroupId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'NotificationSubscriptionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationSubscriptionProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationSubscriptionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationSubscriptionViewModelBase
  implements NotificationSubscriptionProps
{
  protected readonly _props: NotificationSubscriptionProps;
  private _json?: NotificationSubscriptionProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get notificationGroupId(): string {
    return this._props.notificationGroupId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: NotificationSubscriptionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        unitId: this._props.unitId,
        notificationGroupId: this._props.notificationGroupId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationSubscriptionEntities = (state: AppState) =>
  state.entityStores.notificationSubscription.entities;

export const selectNotificationSubscriptionById = (id: string) => (state: AppState) =>
  state.entityStores.notificationSubscription.entities.get(id);

export const selectNotificationSubscriptionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notificationSubscription.meta.get(id);

export const selectNotificationSubscriptionAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationSubscriptionById(id),
    selectNotificationSubscriptionMetadataById(id),
    (notificationSubscription, meta) => ({ notificationSubscription, meta }),
  );

export function selectNotificationSubscriptionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationSubscriptionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationSubscriptionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationSubscriptionEntitiesByName(name: string) {
  return createSelector(selectAllNotificationSubscriptionEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNotificationSubscriptionEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllNotificationSubscriptionEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectNotificationSubscriptionEntitiesByNotificationGroupId(
  notificationGroupId: string,
) {
  return createSelector(selectAllNotificationSubscriptionEntities, (entities) =>
    entities.filter((e) => e.notificationGroupId === notificationGroupId).valueSeq(),
  );
}

export function selectNotificationSubscriptionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationSubscriptionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
